import { useState } from "react";
import axios from "axios";
import { Eye, ShieldAlert, ListChecks, MessagesSquare, Repeat, Loader2, Send, Copy, CheckCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "beobachtung",    label: "Beobachtung",   Icon: Eye,            chip: "01" },
  { key: "risiko",         label: "Risiko",        Icon: ShieldAlert,    chip: "02" },
  { key: "massnahme",      label: "Maßnahme",      Icon: ListChecks,     chip: "03" },
  { key: "kommunikation",  label: "Kommunikation", Icon: MessagesSquare, chip: "04" },
  { key: "evaluation",     label: "Evaluation",    Icon: Repeat,         chip: "05" },
];

export default function PkiDokukette() {
  const [beobachtung, setBeobachtung] = useState("");
  const [kunde, setKunde] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (beobachtung.trim().length < 8) {
      setError("Bitte einen kurzen Satz zur Beobachtung eintragen (mindestens 8 Zeichen).");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/dokukette`, {
        beobachtung: beobachtung.trim(),
        kunde: kunde.trim() || null,
      }, { withCredentials: true });
      setResult(r.data);
      // → 360°-Klientenakte (localStorage)
      if (kunde.trim()) {
        try {
          const store = JSON.parse(localStorage.getItem("dh-klienten-akte") || "{}");
          const arr = store[kunde.trim()] || [];
          arr.unshift({ typ: "dokukette", ts: new Date().toISOString(), dokukette: r.data });
          store[kunde.trim()] = arr.slice(0, 20);
          localStorage.setItem("dh-klienten-akte", JSON.stringify(store));
        } catch { /* noop */ }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Konnte die Dokukette nicht erstellen. Bitte gleich noch mal versuchen.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setBeobachtung("");
    setError(null);
    setCopied(false);
  };

  const copyAll = async () => {
    if (!result) return;
    const txt = STEPS.map((s) => `${s.label.toUpperCase()}\n${result[s.key] || "—"}`).join("\n\n");
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch { /* no-op */ }
  };

  return (
    <div data-testid="pki-dokukette" className="space-y-4">
      <div>
        <h2 className="font-display text-2xl text-sand-800 leading-tight">Dokukette</h2>
        <p className="text-[13.5px] text-sand-600 mt-1 leading-relaxed">
          Von der Beobachtung zur vollständigen Doku in einem Fluss: Beobachtung → Risiko →
          Maßnahme → Kommunikation → Evaluation. Prüffähig, in einem Rutsch.
        </p>
      </div>

      {!result && (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label htmlFor="pki-dk-kunde" className="text-[12px] uppercase tracking-[0.16em] font-semibold text-sand-700">
              Bewohner / Kunde (optional)
            </label>
            <input
              id="pki-dk-kunde"
              type="text"
              value={kunde}
              onChange={(e) => setKunde(e.target.value)}
              data-testid="pki-dokukette-kunde"
              placeholder="z. B. Frau Schmidt"
              className="mt-1 w-full h-11 px-3 rounded-xl bg-white border border-cream-300 focus:border-brand-darkred focus:outline-none text-[15px]"
            />
          </div>
          <div>
            <label htmlFor="pki-dk-beob" className="text-[12px] uppercase tracking-[0.16em] font-semibold text-sand-700">
              Was hast du beobachtet? *
            </label>
            <textarea
              id="pki-dk-beob"
              value={beobachtung}
              onChange={(e) => setBeobachtung(e.target.value)}
              data-testid="pki-dokukette-beobachtung"
              placeholder="z. B. Frau Schmidt zeigt seit gestern zunehmende Unsicherheit beim Aufstehen…"
              rows={5}
              className="mt-1 w-full px-3 py-2.5 rounded-xl bg-white border border-cream-300 focus:border-brand-darkred focus:outline-none text-[15px] leading-relaxed resize-none"
            />
            <div className="mt-1.5 text-[11.5px] text-sand-500">
              Die KI baut die 5-Schritte-Dokukette. Freigabe und finale Formulierung bleiben bei der Pflegefachkraft.
            </div>
          </div>
          {error && (
            <div data-testid="pki-dokukette-error" className="rounded-xl border border-brand-darkred/40 bg-peach-50 p-3 text-[13px] text-brand-darkred">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="pki-dokukette-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white h-12 text-[15px] font-semibold transition-colors disabled:opacity-70"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> Dokukette wird gebaut…</> :
                       <><Send className="h-4 w-4" strokeWidth={1.9} /> Dokukette bauen</>}
          </button>
        </form>
      )}

      {result && (
        <div data-testid="pki-dokukette-result" className="space-y-3">
          {kunde && (
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-darkred">
              Dokukette · {kunde}
            </div>
          )}
          <ol className="space-y-3">
            {STEPS.map(({ key, label, Icon, chip }) => (
              <li key={key} data-testid={`pki-dokukette-step-${key}`}
                className="rounded-2xl bg-white border border-cream-300 p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-sand-800 text-cream-50 inline-flex items-center justify-center shrink-0 font-bold text-[13px]">
                    {chip}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sand-800">
                      <Icon className="h-4 w-4 text-brand-darkred" strokeWidth={1.9} />
                      <span className="font-semibold text-[15px]">{label}</span>
                    </div>
                    <p className="mt-2 text-[14px] text-sand-700 leading-relaxed">
                      {result[key] || <span className="text-sand-500 italic">Keine Angabe</span>}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={copyAll}
              data-testid="pki-dokukette-copy"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sand-800 hover:bg-sand-800/90 text-cream-50 h-12 text-[14.5px] font-semibold transition-colors"
            >
              {copied ? <><CheckCheck className="h-4 w-4" strokeWidth={2} /> In Zwischenablage</> :
                        <><Copy className="h-4 w-4" strokeWidth={1.9} /> Als Text kopieren</>}
            </button>
            <button
              type="button"
              onClick={reset}
              data-testid="pki-dokukette-reset"
              className="w-full inline-flex items-center justify-center rounded-full bg-white border border-cream-300 hover:border-brand-darkred/40 text-sand-800 h-12 text-[14.5px] font-semibold"
            >
              Neue Beobachtung eintragen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
